import type { PageId } from 'constants/pages'
import type {
  Cohort,
  PaymentSystem,
  TimeInterval,
} from 'constants/rootConstants'
import type { FirebaseOptions } from 'firebase/app'
import type { TAnswers } from './common.model'

export interface IUpdateLeadRequest {
  email?: string | null
  extra?: Record<string, unknown>
  firebaseToken?: string | null
}

interface ICreateCohortLeadGeo {
  city: string
  country: string
  ip: null | string
  postalCode: null | string
  region: null | string
  timezone: null | string
}

export interface ICreateCohortLeadRequest {
  cohort: string
  leadId: string
  geo: ICreateCohortLeadGeo
}

export interface ICreateLeadRequest {
  appName: string
  attribution?: {
    fbc: string | null
    fbp: string | null
    utmCampaign: string | null
    utmContent: string | null
    utmMedium: string | null
    utmSource: string | null
    utmTerm: string | null
  }
  cohort: string
  country?: string
  email: string | null
  extra?: Record<string, unknown>
  firebaseToken?: string | null
  locale: string
}

export interface IGetFirebaseCustomTokenResponse {
  customToken: string
}

interface ILeadStepRaw {
  extra: Record<string, unknown>
  hasHeader: boolean
  hasProgressbar: boolean
  isPayment: boolean
  isQuestion: boolean
  isSkippable: boolean
  isSubscriptions: boolean
  isUpsell: boolean
  pageId: PageId
}

export const enum OnboardingType {
  PURCHASE = 'purchase',
  CANCEL_OFFER = 'cancelOffer',
  UPSELL = 'upsell',
  CHAT_UPSELL = 'chatUpsell',
  PDF_UPSELL = 'pdfUpsell',
  PRACTICE_UPSELL = 'practiceUpsell',
}

interface ILeadPurchaseItem {
  id: string
  paymentSystemName: string
  paymentSystemType: PaymentSystem
  planGroup?: string
  planId: string
  planOnboardingType: OnboardingType
  productName: string
}

interface IOnboardingPlanPriceDecoratedPeriod {
  decoratedInterval: string // example: 'week' or '7 days'
  decoratedPrice: string // example: '75$'
  decoratedFullPrice: string // example: '100 USD'
  decoratedDiscountAmount: string // example: '25.00 Dollars'
  decoratedDiscountPercent: string // example: '25%'
}

interface IOnboardingPlanPrice {
  price: number
  periodUnit: TimeInterval
  periodInterval: number
  // contains at least one period data for periodUnit value
  decoratedPeriods: Partial<
    Record<TimeInterval, IOnboardingPlanPriceDecoratedPeriod>
  >
}

interface IOnboardingPlanPriceOffer extends IOnboardingPlanPrice {
  type: 'trial' | 'intro'
}

interface ILeadPlanRaw {
  id: string
  paypalPlanId: string
  stripeIntroPriceId: string
  stripePriceId: string
  tags: string[] // needed for filtering (hidden plans before timer ends)
  onboardingType: OnboardingType // page name within the plan exists
  planGroup?: string // for linking purchases and cancel offers plans with chat upsell plans
  type: string // needed for BE
  isDefault: boolean
  product: string // 'PDF' ...
  prices: {
    main: IOnboardingPlanPrice
    offers: IOnboardingPlanPriceOffer[]
  }
  currency: string
}

type TLeadSubscriptionStatus =
  | 'created'
  | 'trial'
  | 'active'
  | 'cancelled'
  | 'dunning'
  | 'trial_paused'
  | 'active_paused'
  | 'active_cancel_requested'
  | 'trial_cancel_requested'
  | 'active_resuming'
  | 'active_extended'

export interface ILeadSubscriptionItem {
  id: string
  stripeId?: string
  paypalId?: string
  status: TLeadSubscriptionStatus
  paymentSystemName: string
  paymentSystemType: PaymentSystem
  purchaseId: string
  planOnboardingType: OnboardingType
  planId: string
  planGroup?: string
  createdAt?: string
}

export interface ILeadRaw {
  appName: string
  cohort: Cohort | null
  cohortLeadId: string | null
  config: {
    app: {
      amplitudeApiKey: string
      facebookPixelAccessToken: string
      facebookPixelIds: string[] | null
      firebaseConfig: FirebaseOptions | null
      googleAnalyticsId: string
      id: string
    }
    cohort: {
      id: string
      pinterestTagId: string
      currency: string
      plans: Record<OnboardingType, ILeadPlanRaw[]>
      snapPixelId: string
      steps: ILeadStepRaw[]
      tiktokPixelId: string
    }
    payment: {
      id: string
      name: string
      isWeb: boolean
      type: PaymentSystem
      paypalClientId?: string
      stripeAccountId?: string
      stripeAccountName?: string
      stripePublicKey?: string
    }[]
  }
  country: string | null
  email: string | null
  extra: Record<string, unknown>
  firebaseToken: string | null
  id: string
  locale: string
  quizzes: TAnswers
  purchases: ILeadPurchaseItem[] | null
  subscriptions: ILeadSubscriptionItem[] | null
}
